import type { AnswerResponse, CitationResponse } from '@/lib/api'
import CitationList from '@/components/CitationList'
import FeedbackControls from '@/components/FeedbackControls'
import Markdown from '@/components/Markdown'
import type { FeedbackState, FeedbackSubmission } from '@/hooks/useFeedback'
import styles from './AnswerView.module.css'

export type AnswerViewProps =
  | {
      kind: 'streaming'
      partialText: string
      status: string
    }
  | {
      kind: 'ready'
      result: AnswerResponse
      feedbackState?: FeedbackState | undefined
      onFeedback?: ((submission: FeedbackSubmission) => Promise<void>) | undefined
    }
  | {
      // Loaded from a stored conversation: the persisted answer text plus the
      // persisted citation subset (confidence is not persisted server-side).
      kind: 'history'
      text: string
      citations: readonly CitationResponse[]
      feedbackState?: FeedbackState | undefined
      onFeedback?: ((submission: FeedbackSubmission) => Promise<void>) | undefined
    }

export default function AnswerView(props: AnswerViewProps) {
  if (props.kind === 'streaming') {
    return (
      <article className={styles.wrap} aria-busy="true">
        {props.status !== '' && <p className={styles.status}>{props.status}</p>}
        <div className={styles.body}>
          <Markdown>{props.partialText}</Markdown>
          <span className={styles.caret} aria-hidden="true" />
        </div>
      </article>
    )
  }
  if (props.kind === 'history') {
    return <HistoryAnswer {...props} />
  }
  return <ReadyAnswer {...props} />
}

function HistoryAnswer({
  text,
  citations,
  feedbackState,
  onFeedback,
}: Extract<AnswerViewProps, { kind: 'history' }>) {
  return (
    <article className={styles.wrap}>
      <div className={styles.body}>
        <Markdown>{text}</Markdown>
      </div>
      <CitationList citations={citations} />
      {feedbackState !== undefined && onFeedback !== undefined && (
        <FeedbackControls state={feedbackState} onSubmit={onFeedback} />
      )}
    </article>
  )
}

function ReadyAnswer({
  result,
  feedbackState,
  onFeedback,
}: Extract<AnswerViewProps, { kind: 'ready' }>) {
  return (
    <article className={styles.wrap} aria-live="polite">
      <div className={styles.body}>
        <Markdown>{result.answer}</Markdown>
      </div>
      <CitationList citations={result.citations} />
      {feedbackState !== undefined && onFeedback !== undefined && (
        <FeedbackControls state={feedbackState} onSubmit={onFeedback} />
      )}
    </article>
  )
}
